import { Box, Modal, Typography } from "@mui/material";
import { motion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import React from "react";
import { useTranslation } from "react-i18next";

// Animation variants for confirmation modal
const confirmationVariants = {
    hidden: {
        opacity: 0,
        scale: 0.8,
    },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.4,
            ease: "easeOut",
        },
    },
    exit: {
        opacity: 0,
        scale: 0.9,
        transition: {
            duration: 0.2,
        },
    },
};

// Sparkle animation for the success icon
const sparkleVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: {
        scale: [0, 1, 0.8],
        opacity: [0, 1, 0.8],
        transition: {
            duration: 0.6,
        },
    },
};

// Cosmic ring animation for confirmation
const cosmicRingVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
        scale: 1,
        opacity: 0.5,
        transition: {
            duration: 0.8,
            ease: "easeOut",
        },
    },
};

// Text reveal animation
const textRevealVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: (i) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.4,
            delay: i * 0.1 + 0.2,
        },
    }),
};

export default function ConfirmationModal({
                                              open,
                                              onClose,
                                              title,
                                              confimation,
                                              message,
                                              details,
                                              detailsIcon = <CalendarMonthIcon sx={{ color: "rgba(140, 130, 255, 0.8)", fontSize: "1.5rem" }} />,
                                              showDetails = true,
                                          }) {
    const { t } = useTranslation();

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="cosmic-confirmation-modal"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Box
                component={motion.div}
                variants={confirmationVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                sx={{
                    position: "relative",
                    width: "90%",
                    maxWidth: "500px",
                    borderRadius: "16px",
                    background: "rgba(20, 20, 35, 0.9)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(140, 130, 255, 0.3)",
                    padding: "30px 20px",
                    boxShadow: "0 8px 40px rgba(108, 99, 255, 0.4)",
                    overflow: "hidden",
                    textAlign: "center",
                }}
            >
                {/* Cosmic ring background effect */}
                <Box
                    component={motion.div}
                    variants={cosmicRingVariants}
                    initial="hidden"
                    animate="visible"
                    sx={{
                        position: "absolute",
                        width: "300px",
                        height: "300px",
                        borderRadius: "50%",
                        border: "1px solid rgba(140, 130, 255, 0.4)",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 0,
                    }}
                />

                {/* Success icon with orbital circle */}
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        mb: 3,
                        mt: 1,
                    }}
                >
                    {/* Orbital circle */}
                    <Box
                        component={motion.div}
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{
                            duration: 6,
                            repeat: Infinity,
                            ease: "linear",
                        }}
                        sx={{
                            position: "absolute",
                            width: "100px",
                            height: "100px",
                            borderRadius: "50%",
                            border: "1px dashed rgba(140, 130, 255, 0.3)",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                        }}
                    />

                    {/* Success icon */}
                    <Box
                        component={motion.div}
                        variants={sparkleVariants}
                        initial="initial"
                        animate="animate"
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            background: "rgba(20, 20, 35, 0.8)",
                            borderRadius: "50%",
                            padding: "10px",
                            boxShadow: "0 0 20px rgba(132, 59, 254, 0.5)",
                        }}
                    >
                        <CheckCircleIcon
                            sx={{
                                color: "#843bfe",
                                fontSize: "60px",
                                filter: "drop-shadow(0 0 8px rgba(132, 59, 254, 0.7))",
                            }}
                        />
                    </Box>
                </Box>

                {/* Text content */}
                <Box sx={{ position: "relative", zIndex: 2 }}>
                    <motion.div
                        custom={0}
                        variants={textRevealVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <Typography
                            sx={{
                                fontSize: "1.8rem",
                                fontWeight: 700,
                                background: "linear-gradient(90deg, #6366f1 0%, #843bfe 100%)",
                                backgroundSize: "200% auto",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                mb: 2,
                                animation: "gradient 3s linear infinite",
                                "@keyframes gradient": {
                                    "0%": {
                                        backgroundPosition: "0% center",
                                    },
                                    "100%": {
                                        backgroundPosition: "200% center",
                                    },
                                },
                            }}
                        >
                            {t(title)}
                        </Typography>
                    </motion.div>

                    <motion.div
                        custom={1}
                        variants={textRevealVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <Typography
                            sx={{
                                fontSize: "1rem",
                                color: "rgba(255, 255, 255, 0.9)",
                                mb: 2,
                            }}
                        >
                            {t(message)}
                        </Typography>
                    </motion.div>

                    {/* Details section */}
                    {showDetails && details && (
                        <motion.div
                            custom={2}
                            variants={textRevealVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    background: "rgba(140, 130, 255, 0.1)",
                                    borderRadius: "8px",
                                    py: 1,
                                    px: 2,
                                    mb: 2,
                                    border: "1px solid rgba(140, 130, 255, 0.2)",
                                }}
                            >
                                {detailsIcon}
                                <Typography sx={{ color: "rgba(255, 255, 255, 0.9)" }}>
                                    {details}
                                </Typography>
                            </Box>
                        </motion.div>
                    )}

                    <motion.div
                        custom={3}
                        variants={textRevealVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <Typography
                            sx={{
                                fontSize: "0.9rem",
                                color: "rgba(255, 255, 255, 0.7)",
                                fontStyle: "italic",
                            }}
                        >
                            {confimation}
                        </Typography>
                    </motion.div>
                </Box>

                {/* Sparkles */}
                {[...Array(5)].map((_, i) => (
                    <Box
                        key={i}
                        component={motion.div}
                        initial={{
                            opacity: 0,
                            scale: 0,
                            x: Math.random() * 80 - 40,
                            y: Math.random() * 80 - 40,
                        }}
                        animate={{
                            opacity: [0, 0.7, 0],
                            scale: [0, 0.8, 0],
                            x: Math.random() * 100 - 50,
                            y: Math.random() * 100 - 50,
                        }}
                        transition={{
                            duration: 1.5 + Math.random(),
                            delay: i * 0.15,
                            repeat: Infinity,
                            repeatDelay: Math.random(),
                        }}
                        sx={{
                            position: "absolute",
                            width: 3 + Math.random() * 4,
                            height: 3 + Math.random() * 4,
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(140, 130, 255, 0.8) 0%, rgba(140, 130, 255, 0) 70%)",
                            boxShadow: "0 0 6px rgba(140, 130, 255, 0.6)",
                            zIndex: 0,
                        }}
                    />
                ))}
            </Box>
        </Modal>
    );
}